import React, { useCallback, useEffect, useRef, useState } from "react";
import "./Products.css";
import axiosConfig from "../../Service/axiosConfig";
import Product from "./Product";
const priceRanges = [
  { label: "All Prices", min: "", max: "" },
  { label: "Under $5,000", min: "", max: "5000" },
  { label: "$5,000 - $10,000", min: "5000", max: "10000" },
  { label: "$10,000 - $15,000", min: "10000", max: "15000" },
  { label: "Above $15,000", min: "15000", max: "" },
];
const DEFAULT_SORT = "Relevance";

const normalizeProducts = (items = []) =>
  [...items].sort((a, b) => (a.sort ?? 9999) - (b.sort ?? 9999));

const sortProducts = (items, sortBy) => {
  const normalizedItems = [...items];

  switch (sortBy) {
    case "Price (Low to High)":
      return normalizedItems.sort((a, b) => {
        const priceA = parseFloat(a.prices?.sale_offer_price || a.offer_price || a.price || 0);
        const priceB = parseFloat(b.prices?.sale_offer_price || b.offer_price || b.price || 0);
        return priceA - priceB;
      });
    case "Price (High to Low)":
      return normalizedItems.sort((a, b) => {
        const priceA = parseFloat(a.prices?.sale_offer_price || a.offer_price || a.price || 0);
        const priceB = parseFloat(b.prices?.sale_offer_price || b.offer_price || b.price || 0);
        return priceB - priceA;
      });
    case "Discount (High to Low)":
      return normalizedItems.sort((a, b) => {
        const discountA = parseFloat(a.discount_percentage || a.prices?.discount_percent || 0);
        const discountB = parseFloat(b.discount_percentage || b.prices?.discount_percent || 0);
        return discountB - discountA;
      });
    case "Name (A to Z)":
      return normalizedItems.sort((a, b) => {
        const nameA = (a.name || a.product?.name || "").toLowerCase();
        const nameB = (b.name || b.product?.name || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });
    default:
      return normalizeProducts(normalizedItems);
  }
};

const normalizeNextUrl = (url) =>
  url?.startsWith("http:") ? url.replace(/^http:/, "https:") : url;

const ProductsList = ({ subcategory_id, promo_id, friendlyurl, subcategoryName, listingType }) => {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [baseProducts, setBaseProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);
  const [nextUrl, setNextUrl] = useState(null);
  const [priceRange, setPriceRange] = useState(priceRanges[0]);
  const isFetchingMoreRef = useRef(false);
  const loadMoreRef = useRef(null);
  const buildProductsUrl = useCallback(() => {
    const storeId = localStorage.getItem("storeId") || "";

    if (promo_id) {
      return `/catlog/promotional/${promo_id}/items/?subcategory=${subcategory_id}&store=${storeId}`;
    }
    return `/catlog/category-variants/?search=&category=&price_min=${priceRange.min}&price_max=${priceRange.max}&options=&subcategory=${encodeURIComponent(
      subcategoryName
    )}&store_id=${storeId}&listing_type=${listingType}&is_suspended=false&page_size=20`; // ← ONLY THIS LINE CHANGED
  }, [promo_id, subcategory_id, subcategoryName, priceRange.min, priceRange.max, listingType]); // ← ADD listingType here too

  const fetchPage = useCallback(async (url) => {
    const response = await axiosConfig.get(normalizeNextUrl(url));
    return {
      items: normalizeProducts(response.data?.results || []),
      nextUrl: response.data?.next || null,
    };
  }, []);

  const loadProducts = useCallback(async () => {
    if (!subcategory_id) {
      setBaseProducts([]);
      setNextUrl(null);
      return;
    }

    setLoading(true);
    setNextUrl(null);

    try {
      const { items, nextUrl: fetchedNextUrl } = await fetchPage(buildProductsUrl());
      setBaseProducts(items);
      setNextUrl(fetchedNextUrl);
    } catch (error) {
      console.error("Error fetching products:", error);
      setBaseProducts([]);
    } finally {
      setLoading(false);
    }
  }, [buildProductsUrl, fetchPage, subcategory_id]);

  const loadMore = useCallback(async () => {
    if (!nextUrl || isFetchingMoreRef.current) {
      return;
    }

    isFetchingMoreRef.current = true;
    setLoadingMore(true);

    try {
      const { items, nextUrl: fetchedNextUrl } = await fetchPage(nextUrl);
      setBaseProducts((currentItems) =>
        normalizeProducts([...currentItems, ...items])
      );
      setNextUrl(fetchedNextUrl);
    } catch (error) {
      console.error("Error loading more products:", error);
    } finally {
      isFetchingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [fetchPage, nextUrl]);
  // Load all remaining products for sorting
  const loadAllRemainingProducts = async () => {
    let allProducts = [...baseProducts];
    let currentNextUrl = nextUrl;

    while (currentNextUrl) {
      try {
        const response = await axiosConfig.get(normalizeNextUrl(currentNextUrl));
        allProducts = [
          ...allProducts,
          ...(response.data?.results || []),
        ];
        currentNextUrl = response.data?.next;
      } catch (error) {
        console.error("Error loading remaining products:", error);
        break;
      }
    }

    setBaseProducts(normalizeProducts(allProducts));
    setNextUrl(null);
  };
  // Handle sort change with lazy loading
  const handleSortChange = async (e) => {
    const value = e.target.value;

    // If sorting by anything other than Relevance and we have more products to load
    if (value !== "Relevance" && nextUrl) {
      setLoading(true);
      await loadAllRemainingProducts();
      setLoading(false);
    }

    setSortBy(value);
  };
  useEffect(() => {
    setSortBy(DEFAULT_SORT);
    loadProducts();
  }, [loadProducts, priceRange]);

  useEffect(() => {
    setProducts(sortProducts(baseProducts, sortBy));
  }, [baseProducts, sortBy]);

  useEffect(() => {
    const loadMoreElement = loadMoreRef.current;
    if (!loadMoreElement || !nextUrl || loadingMore) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(loadMoreElement);
    return () => observer.disconnect();
  }, [loadMore, nextUrl, loadingMore]);

  return (
    <div className="products-container">
      {loading ? (
        <div className="loader-container">
          <img src={require("../images/loader.gif")} alt="Loading..." />
        </div>
      ) : (
        <>
          <div className="inner-container">
            <div className="top-info-container">
              <div className="d-flex justify-content-between align-items-start flex-wrap">
                <div>
                  <p className="subcategory-title mb-0" style={{ textTransform: 'capitalize' }}>
                    {subcategoryName || friendlyurl}
                  </p>
                </div>
                <div className="d-flex gap-3 flex-wrap">
                  <div className="d-flex flex-column">
                    <label htmlFor="sortby" className="form-label">
                      Sort By
                    </label>
                    <select
                      className="Selectbar"
                      id="sortby"
                      name="sortby"
                      value={sortBy}
                      onChange={handleSortChange}
                    >
                      <option>Relevance</option>
                      <option>Price (Low to High)</option>
                      <option>Price (High to Low)</option>
                      <option>Discount (High to Low)</option>
                      <option>Name (A to Z)</option>
                    </select>
                  </div>

                  <div className="d-flex flex-column">
                    <label htmlFor="pricerange" className="form-label">
                      Price Range
                    </label>
                    <select
                      className="Selectbar"
                      id="pricerange"
                      name="pricerange"
                      value={priceRange.label}
                      onChange={(e) => {
                        const selected = priceRanges.find(range => range.label === e.target.value);
                        if (selected) {
                          setPriceRange(selected);
                        }
                      }}
                    >
                      {priceRanges.map((range) => (
                        <option key={range.label} value={range.label}>
                          {range.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {products.length > 0 ? (
            <div className="catagery-items-grid">
              {products.map((itemObj, index) => (
                <div className="catagery-item-product" key={itemObj.id || index}>
                  <Product item={itemObj} index={index} listingType={listingType} />
                </div>
              ))}

              {loadingMore && (
                <div className="loader-container" style={{ gridColumn: '1 / -1' }}>
                  <img src={require("../images/loader.gif")} alt="Loading..." />
                </div>
              )}
              {nextUrl && !loadingMore && (
                <div 
                  ref={loadMoreRef} 
                  className="cb-load-more-sentinel" 
                  style={{ gridColumn: '1 / -1', padding: '20px', textAlign: 'center', height: '100px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                >
                  <button 
                    type="button"
                    className="btn btn-outline-secondary" 
                    onClick={loadMore}
                    style={{ minWidth: '150px' }}
                  >
                    Load More
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="catagery-items-grid">
              <p className="subcategory-title">No products available.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export { ProductsList };
